import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Linkedin, Globe, ArrowRight, Image as ImageIcon, FileText, Video, Link as LinkIcon, Figma, BookOpen, Music, Lock } from "lucide-react";
import DemoLayout from "@/components/DemoLayout";
import { normalizePiece, WORK_TYPE_LABEL } from "@/lib/portfolio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PIECE_ICONS = { image: ImageIcon, video: Video, pdf: FileText, url: LinkIcon, figma: Figma, case_study: BookOpen, audio: Music, on_request: Lock };

const TABS = ["basics", "experience", "portfolio", "voice", "preferences"];

export default function DemoProfile() {
  const [params] = useSearchParams();
  const personaId = params.get("persona");
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [tab, setTab] = useState("basics");

  useEffect(() => {
    if (!personaId) { navigate("/demo", { replace: true }); return; }
    axios.get(`${API}/demo/profile/${personaId}`)
      .then((r) => setProfile(r.data.profile))
      .catch(() => navigate("/demo", { replace: true }));
  }, [personaId, navigate]);

  if (!profile) {
    return <DemoLayout><div className="p-12 text-center text-muted-foreground text-xs uppercase tracking-[0.3em]">LOADING<span className="blink-caret"/></div></DemoLayout>;
  }

  return (
    <DemoLayout persona={profile}>
      <div className="max-w-5xl mx-auto px-5 md:px-12 py-6 sm:py-10">
        <div className="terminal-label text-primary">[ PROFILE.SETTINGS ]</div>
        <h1 className="font-display text-2xl sm:text-4xl md:text-5xl uppercase font-bold tracking-tight mt-2 mb-6 sm:mb-8">Profile</h1>

        <div className="flex md:grid md:grid-cols-5 gap-px mb-6 sm:mb-8 border border-border overflow-x-auto md:overflow-visible -mx-5 px-5 md:mx-0 md:px-0">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`demo-profile-tab-${t}`}
              className={`flex-shrink-0 p-3 text-[10px] uppercase tracking-[0.22em] font-mono transition-colors ${tab === t ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:text-primary"}`}
            >{t}</button>
          ))}
        </div>

        {tab === "basics" && <Basics p={profile} />}
        {tab === "experience" && <Experience p={profile} />}
        {tab === "portfolio" && <Portfolio p={profile} />}
        {tab === "voice" && <Voice p={profile} />}
        {tab === "preferences" && <Preferences p={profile} />}

        <div className="mt-10 panel p-5 border-primary/30 text-center" style={{ borderColor: "hsl(var(--primary) / 0.3)" }}>
          <p className="text-sm text-foreground/85 mb-3">In the real product every field is editable and Sisyphean re-tailors materials automatically when anything changes.</p>
          <Link to={`/demo/dashboard?persona=${personaId}`} className="btn-secondary inline-flex items-center gap-2 text-[11px]">Back to feed <ArrowRight className="w-3 h-3" /></Link>
        </div>
      </div>
    </DemoLayout>
  );
}

function Field({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono mb-1">{label}</div>
      <div className={`text-sm text-foreground/90 ${mono ? "font-mono" : ""}`}>{value || "-"}</div>
    </div>
  );
}

function Basics({ p }) {
  return (
    <div className="panel p-5 sm:p-8 space-y-6">
      <div className="flex items-start gap-5 sm:gap-8 flex-wrap">
        {p.headshot_url && <img src={p.headshot_url} alt={p.name} className="w-24 h-24 sm:w-28 sm:h-28 object-cover border border-foreground/20" />}
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-5 min-w-[260px]">
          <Field label="Name" value={p.name} />
          <Field label="Title" value={p.title} />
          <Field label="Location" value={p.location} />
          <Field label="Discipline" value={p.discipline} />
        </div>
      </div>
      <Field label="Bio" value={p.bio} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono mb-1">LinkedIn</div>
          {p.linkedin_url ? <a href={p.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary inline-flex items-center gap-2"><Linkedin className="w-3 h-3" /> {p.linkedin_url}</a> : <span className="text-sm text-muted-foreground">-</span>}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono mb-1">Behance</div>
          {p.behance_url ? <a href={p.behance_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary inline-flex items-center gap-2"><Globe className="w-3 h-3" /> {p.behance_url}</a> : <span className="text-sm text-muted-foreground">-</span>}
        </div>
      </div>
    </div>
  );
}

function Experience({ p }) {
  return (
    <div className="space-y-px border border-border bg-border">
      <div className="bg-background p-5 sm:p-7">
        <div className="terminal-label text-primary mb-3">[ SKILLS ]</div>
        <div className="flex flex-wrap gap-2">
          {(p.skills || []).map((s) => <span key={s} className="text-xs font-mono px-2 py-1 border border-foreground/20">{s}</span>)}
        </div>
      </div>
      <div className="bg-background p-5 sm:p-7">
        <div className="terminal-label text-primary mb-4">[ WORK.HISTORY ]</div>
        <div className="space-y-5">
          {(p.work_history || []).map((w) => (
            <div key={w.id} className="border-l-2 border-primary/40 pl-4">
              <div className="font-display text-sm uppercase font-semibold tracking-tight">{w.role}</div>
              <div className="text-xs text-muted-foreground">{w.company} · {w.start_year} - {w.end_year}</div>
              <p className="text-sm text-foreground/85 mt-2 leading-relaxed">{w.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-background p-5 sm:p-7">
        <div className="terminal-label text-primary mb-4">[ EDUCATION ]</div>
        <div className="space-y-3">
          {(p.education || []).map((e, i) => (
            <div key={i}>
              <div className="text-sm font-medium">{e.school}</div>
              <div className="text-xs text-muted-foreground">{e.degree} · {e.start_year} - {e.end_year}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Portfolio({ p }) {
  return (
    <div className="panel p-5 sm:p-7">
      <div className="flex items-center justify-between mb-5">
        <div className="terminal-label text-primary">[ PORTFOLIO.PIECES / {p.portfolio_pieces?.length || 0} ]</div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono">In real app: add / edit / delete</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(p.portfolio_pieces || []).map((rawP) => {
          const piece = normalizePiece(rawP);
          const Icon = PIECE_ICONS[piece.piece_type] || ImageIcon;
          const thumb = rawP.image_url || rawP.image_urls?.[0]
            || rawP.case_study?.problem?.image_urls?.[0]
            || rawP.case_study?.process?.image_urls?.[0]
            || rawP.case_study?.outcome?.image_urls?.[0];
          return (
            <div key={piece.id} className="panel p-3" data-testid={`demo-profile-piece-${piece.id}`}>
              <div className="aspect-video bg-surface mb-3 flex items-center justify-center overflow-hidden">
                {thumb ? <img src={thumb} alt={piece.title} className="w-full h-full object-cover" /> : <Icon className="w-6 h-6 text-muted-foreground" strokeWidth={1.25} />}
              </div>
              <div className="text-sm font-mono font-medium truncate">{piece.title}</div>
              <div className="flex items-center justify-between gap-2 mt-1">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground truncate">{WORK_TYPE_LABEL[piece.work_type] || piece.work_type}</div>
                {piece.visibility === "on_request" && <span className="text-[9px] tracking-[0.18em] uppercase text-primary font-mono">on req.</span>}
              </div>
              {piece.description && <p className="text-[11px] text-foreground/70 mt-2 line-clamp-3 leading-relaxed">{piece.description}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Voice({ p }) {
  const v = p.writing_voice_analysis || {};
  return (
    <div className="panel p-5 sm:p-7 space-y-5">
      <div className="terminal-label text-primary">[ WRITING.VOICE.ANALYSIS ]</div>
      <Field label="Tone" value={v.tone} />
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono mb-2">Voice traits</div>
        <div className="flex flex-wrap gap-2">
          {(v.voice_traits || []).map((t) => <span key={t} className="text-xs font-mono px-2 py-1 border border-foreground/20">{t}</span>)}
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground italic">In the real app you upload writing samples and Sisyphean derives this voice profile, then mirrors it in cover letters.</p>
    </div>
  );
}

function Preferences({ p }) {
  const pref = p.preferences || {};
  return (
    <div className="panel p-5 sm:p-7 space-y-5">
      <div className="terminal-label text-primary">[ JOB.PREFERENCES ]</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Desired roles" value={(pref.desired_roles || []).join(", ")} />
        <Field label="Industries" value={(pref.industries || []).join(", ")} />
        <Field label="Company sizes" value={(pref.company_sizes || []).join(", ")} />
        <Field label="Work modes" value={(pref.work_modes || []).join(", ")} />
        <Field label="Target cities" value={(pref.target_cities || []).join(", ")} />
      </div>
      <Field label="Things I enjoy" value={pref.enjoy_factors} />
      <Field label="Things to avoid" value={pref.avoid_factors} />
    </div>
  );
}
